import React, { memo, useCallback, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Box, Card } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Tabs,
  Tab,
  RangeSelector,
  performanceIntervalTypes,
  ComparisonValue,
  NonProContainer,
  SelectCompare,
} from "@components";
import {
  DividendsChart,
  PerformanceChart,
  ValuationChart,
} from "@components/Chart/highcharts";
import { useDividendsChartData } from "@api/v1/hooks/performance/useDividendsChartData";
import { useInvestmentsChartDataTotal } from "@api/v1/hooks/performance/useInvestmentsChartDataTotal";
import { useRangeSelectControl } from "@hooks";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider.tsx";
import { PerformanceChartTypes } from "./types";

const DashboardPerformanceWrapper = styled(Card)`
  padding: ${({ theme }) => theme.spacing(4, 4, 5)};
  min-height: 236px;
  overflow: visible;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const TabPanel = styled.div`
  padding-top: ${({ theme }) => theme.spacing(4)};
`;

const StyledTabs = styled(Tabs)<{ centered?: boolean }>`
  .MuiTabs-flexContainer {
    justify-content: ${({ centered }) =>
      !centered ? "space-between" : "center"};
  }
`;

interface Props {
  showCompare?: boolean;
  showDividendsToggle?: boolean;
  showCapitalToggle?: boolean;
  centered?: boolean;
  chartHeight?: number;
}

export const DashboardPerformance = memo(
  ({
    showCompare,
    showDividendsToggle,
    showCapitalToggle,
    centered = true,
    chartHeight,
  }: Props) => {
    const { t } = useTranslation();
    const { groupedAccountIds, isFetching: areAccountsLoading } =
      useDashboardSelectedGroupsContext();
    const { range, intervalType, selectInterval, customIntervalChange } =
      useRangeSelectControl({
        firstDate: new Date(),
        id: "dashboard",
      });

    const [displayedChart, setDisplayedChart] =
      useState<PerformanceChartTypes>("valuation");
    const [comparedTicker, setComparedTicker] = useState<ComparisonValue>();

    const accountIds = useMemo(
      () => [
        ...groupedAccountIds.cashIds,
        ...groupedAccountIds.creditCardIds,
        ...groupedAccountIds.depotIds,
      ],
      [groupedAccountIds]
    );

    const {
      data: chartData,
      isFetching: isLoadingChartData,
      isFetched: isChartDataFetched,
    } = useInvestmentsChartDataTotal({
      accountIds,
      startDate: range.startDate,
      endDate: range.endDate,
      chartType: displayedChart,
      comparedTicker:
        displayedChart === "performance" ? comparedTicker : undefined,
      areAccountsLoading,
    });

    const {
      chartData: dividendsChartData,
      isFetching: isDividendsLoading,
      isFetched: isDividendsFetched,
    } = useDividendsChartData({
      accountIds,
      startDate: range?.startDate!,
      endDate: range?.endDate,
      interval: intervalType,
      enabled: displayedChart === "dividends",
    });

    const handleTabChange = (tabKey: string) => {
      const isSwitchingToDividends = tabKey === "dividends";
      const isSwitchingAwayFromDividends =
        displayedChart === "dividends" && !isSwitchingToDividends;

      setDisplayedChart(tabKey as PerformanceChartTypes);

      if (
        (intervalType === "oneMonth" || intervalType === "oneWeek") &&
        isSwitchingToDividends
      ) {
        selectInterval("YTD");
      }

      if (
        isSwitchingAwayFromDividends &&
        intervalType === "custom" &&
        range?.endDate
      ) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const end = new Date(range.endDate);
        end.setHours(0, 0, 0, 0);
        if (end > today) {
          customIntervalChange(range.startDate, new Date());
        }
      }
    };

    const renderRangeSelector = useCallback(
      () => (
        <Box mb={4}>
          <RangeSelector
            size="small"
            range={intervalType === "custom" ? range : undefined}
            onSelect={selectInterval}
            onCustomSelect={customIntervalChange}
            intervalType={intervalType}
            intervalTypes={performanceIntervalTypes}
            allowFutureInCustom={displayedChart === "dividends"}
            disabledIntervalTypes={
              displayedChart === "dividends" ? ["oneWeek", "oneMonth"] : []
            }
          />
        </Box>
      ),
      [
        customIntervalChange,
        intervalType,
        range,
        selectInterval,
        displayedChart,
      ]
    );

    return (
      <DashboardPerformanceWrapper>
        <StyledTabs
          key={displayedChart}
          onChange={handleTabChange}
          defaultKey={displayedChart}
          centered={centered}
        >
          <Tab
            label={t("dashboardPage.dashboard.performance.tabs.value.title")}
            key="valuation"
          >
            <TabPanel>
              {renderRangeSelector()}
              <ValuationChart
                chartData={chartData}
                isFetching={areAccountsLoading || isLoadingChartData}
                isFetched={isChartDataFetched}
                showInvestedCapitalToggle={showCapitalToggle}
                height={chartHeight}
              />
            </TabPanel>
          </Tab>
          <Tab
            label={t(
              "dashboardPage.dashboard.performance.tabs.performance.title"
            )}
            key="performance"
          >
            <TabPanel>
              {renderRangeSelector()}
              <PerformanceChart
                chartData={chartData}
                isFetched={isChartDataFetched}
                isFetching={areAccountsLoading || isLoadingChartData}
                comparedTicker={comparedTicker}
                showDividendsToggle={showDividendsToggle}
                height={chartHeight}
              />
            </TabPanel>
          </Tab>
          <Tab
            label={t("dashboardPage.dashboard.performance.tabs.dividend.title")}
            key="dividends"
          >
            <TabPanel>
              {renderRangeSelector()}
              <DividendsChart
                chartData={dividendsChartData}
                interval={intervalType}
                isFetching={areAccountsLoading || isDividendsLoading}
                isFetched={!accountIds?.length ? true : isDividendsFetched}
                height={chartHeight}
              />
            </TabPanel>
          </Tab>
        </StyledTabs>
        <Box>
          <NonProContainer>
            {showCompare && (
              <SelectCompare
                value={comparedTicker}
                onChange={(value?: ComparisonValue) => {
                  setComparedTicker(value);
                  if (value) {
                    handleTabChange("performance");
                  }
                }}
              />
            )}
          </NonProContainer>
        </Box>
      </DashboardPerformanceWrapper>
    );
  }
);
